import { useContext, useEffect, useState } from 'react';
import { Page, Spacer } from '../../components';
import { MutationStatus, useAppStorage, useMutation, useObservable } from '@aesop-fables/scrinium';
import { UserCompartments, userStorageKey } from '../../data/user';
import { UserData } from '../../models/UserData';
import { ToastbarContext } from '../../App';
import { useLoading } from '../../hooks/useLoading';
import { GroupCompartments, groupStorageKey } from '../../data/group';
import { GroupData } from '../../models/GroupData';
import { Toast } from '../../models/Toast';
import { DeleteGroup, SaveGroupData } from '../../data/group/mutations';
import AdminTierHeader from '../multitier/AdminTierHeader';
import { AdminPageProps } from '../multitier/AdminDashboard';
import { GroupMetricsData } from '../../models/MetricsData';
import { GroupListView } from './GroupListView';

const GroupPage: React.FC<AdminPageProps> = ({
  select,
  selectTeam,
  selectOrganization,
  setOrgStatus,
  setIsNavigatingToOrg,
}) => {
  const appStorage = useAppStorage();
  const { setToast } = useContext(ToastbarContext);
  const { setLoading } = useLoading();
  const [tryAgain, setTryAgain] = useState<boolean>(false);

  const saveGroupData = useMutation(new SaveGroupData());
  const deleteGroup = useMutation(new DeleteGroup());

  const userDataCache = appStorage.retrieve<UserCompartments>(userStorageKey);
  const groupDataCache = appStorage.retrieve<GroupCompartments>(groupStorageKey);

  const userData = useObservable(userDataCache.observe$<UserData>('user'));
  const allGroups = useObservable(groupDataCache.observe$<GroupData[]>('allGroups'));
  const allMetrics = useObservable(groupDataCache.observe$<GroupMetricsData[]>('allMetrics'));

  const onCreate = async (values: GroupData) => {
    const { name } = values;
    await saveGroupData.action({
      name: name,
    });
  };

  useEffect(() => {
    if (!tryAgain) {
      return;
    }
    if (deleteGroup.error) {
      setLoading(false);
      setToast(
        new Toast({
          message: 'Error removing group',
          severity: 'error',
          open: true,
        }),
      );
      setTryAgain(false);
    } else if (deleteGroup.status === MutationStatus.Complete) {
      setLoading(false);
      setToast(new Toast({ message: 'Group removed!', severity: 'success', open: true }));
      setTryAgain(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteGroup, tryAgain]);

  if (allGroups === undefined || allMetrics === undefined) {
    return null;
  }

  return (
    <Page title={userData?.firstName ? `Hi, ${userData.firstName}!` : 'Hi!'}>
      <Spacer height='sm' />
      <AdminTierHeader onCreate={onCreate} hideCreate tier='group' />
      <GroupListView
        groupList={allGroups}
        select={select}
        selectTeam={selectTeam}
        allMetrics={allMetrics}
        selectOrganization={selectOrganization}
        setOrgStatus={setOrgStatus}
        setIsNavigatingToOrg={setIsNavigatingToOrg}
      />
    </Page>
  );
};

export default GroupPage;
